import { useMemo } from 'react'
import { AspectRatio, Box } from '@mantine/core'
import LazyMap from './_LazyMap'

// THE STATION PAGE'S MAP IS THE APP'S MAP (plan "Web map surfaces" section 2.2,
// build item 4; Randy's rulings 3 and 5.1, 2026-09-16; superseded 2026-09-16:
// "I don't think we need the map button on station pages - go is enough" —
// which turned out to mean the door everywhere, not just here; see below).
//
// It used to be a second implementation — TideMap.jsx and CurrentMap.jsx, a
// plain basemap with the page's own station on it and a checkbox pair for wind
// and seas — and that is the thing being retired. Two maps drawn by two files
// drift: the markers were the #427 set in one and a fixed 1.0 icon in the
// other, the station labels went navy-on-navy in dark mode here months after
// the app had fixed it, and every capability the app grew (the popup shell, the
// scheme swap, the language pass, the crash context) had to be ported by hand
// or quietly not be.
//
// So there is one map now, composed for this page:
//
//   ground  Standard Map, both types (ruling 3: "maybe standard no depth").
//           The depth chart is one tap away through GO; the station page's
//           map is the calmer one.
//   viz     the KIND OF STATION the page is about. Randy, 2026-09-16: "I
//           thought we were going to do the current graphics for current
//           stations. So wind and waves on tides and then the current
//           visualization for currents." A current station page animates the
//           currents; a tide station page keeps Wind & Waves, which is what it
//           has shown for years and what the weather block beside it is about.
//   camera  the station, at z12, the zoom these pages have always opened on.
//   picker  none. The picker exists in exactly one place.
//   player  none. The page is about now.
//   legend  none. Moving Currents runs PARTICLES ONLY here (particlesOnly),
//           so there is no colour field for a current-speed ramp to explain
//           and no card for it to sit on — the panel is all map.
//   locate  none. GO is the only way off this map that asks where the reader is.
//   URL     untouched. A station page's address is its permalink, so the map
//           mounts with `syncUrl` false and writes no hash — maximizing it
//           does not change that (see `expandInPlace` below).
//
// THE COLUMN IS GO AND EXPAND, and nothing else — the same two controls a
// chart page draws, in the same two slots (feedback_reuse_the_control_family:
// "when in doubt please don't invent something new design wise"). GO is
// `goEntry="station"`, its own analytics surface. Expand is the SAME mechanism
// the chart page and the home map use — `expandInPlace`, the same map instance
// resized to fill the viewport, ✕ / Escape / the button itself to collapse —
// with no `expandedPath`/`collapsedPath` pair, exactly like the chart page, so
// maximizing writes no URL (see `urlExpand` in ExploreMap: it is only true
// where the caller gives both paths, which neither landing-page composition
// does). No new component and no new slot geometry — `hasExpandButton` and the
// maximize button are ExploreMap's own, unconditional on `stationMode`.
//
// THE DOOR IS GONE (Randy, 2026-09-16, same day, correcting the first cut of
// this change): "I don't think we need the map button on station pages" turned
// out to mean every page, not only this one, once expand was added everywhere
// as the door's replacement — a reader who wants the full app now gets there
// by maximizing and pressing GO from there, rather than by a second control
// that only ever opened `/explore`. `openInMap`, the door's glyph and
// `open_in_map` in the catalogs are deleted, not merely unpassed — see
// ExploreMap.jsx and the locale files.

export const STATION_MAP_ZOOM = 12

// THE PRESET MATRIX, as data. One object rather than a ternary at the call
// site, so "what does a current station show" is answerable by reading a table
// and changing it is one edit.
export const STATION_PRESETS = {
  tide: { ground: 'standard', viz: 'wind' },
  current: { ground: 'standard', viz: 'currents' },
}

export default function StationMapPanel({ kind, station, layerCoverage = null }) {
  // THE `weather_panel` GATE IS GONE, which the plan allows once the port is
  // verified ("the `weather_panel` gate becomes the gate for the whole
  // visualization on station pages until the port is verified, then goes
  // away"). It was carried on this branch, and the verification is
  // test/e2e/station-page-map.spec.mts run locally against real signed
  // `weather/*` frames: both fields install AND put ink on the water on a tide
  // page, the water field does on a current page, both pages place their
  // markers and promote their own station. (That verification predates the
  // door's removal and said nothing about it either way.)
  //
  // Keeping it would also have been a category error. That flag gates the
  // marine-weather PANEL — the block of wind, seas and warnings beside this map
  // — and it still does, untouched. A current station's Moving Currents has
  // nothing to do with the weather feed: it reads the station snapshot, the
  // same one the home map reads. Gating it on a weather flag would have made
  // one switch mean two things.
  //
  // Stable identities: `selectedStation` and `stationPreset` are objects, and a
  // fresh one per render would re-run the map's selection effect on every
  // parent render (the weather fetch alone causes several).
  const preset = useMemo(() => STATION_PRESETS[kind] ?? STATION_PRESETS.tide, [kind])
  const selected = useMemo(() => ({ type: kind, id: station.id }), [kind, station.id])
  const view = useMemo(
    () => ({ latitude: station.lat, longitude: station.lng, zoom: STATION_MAP_ZOOM }),
    [station.lat, station.lng],
  )

  return (
    // Unchanged from the panel this replaces: a square, rounded and clipped
    // (MapLibre's canvas has no corner radius of its own). The geometry is what
    // decides the phone layout, so it is deliberately the one thing item 4 does
    // not touch. Maximized, ExploreMap's own root goes `position: fixed` and
    // covers the viewport (mapRootStyle) — this box's clip has nothing left to
    // clip, exactly as it has nothing to clip on a maximized chart page.
    <Box style={{ borderRadius: 12, overflow: 'hidden' }}>
      <AspectRatio ratio={1}>
        <LazyMap
          load={() => import('../explore/ExploreMap')}
          surface="station"
          height="100%"
          stationPreset={preset}
          selectedStation={selected}
          initialView={view}
          // The Go button's own analytics surface, and the thing that draws it
          // (plan section 2.3: one button, the same slot, every surface).
          goEntry="station"
          // The GO button picks its ground from the coverage manifest, the same
          // way it does on every other surface — see goLayersFor.
          layerCoverage={layerCoverage}
          // NEVER ASK ON A STATION PAGE. The reader came for a place they named,
          // not for where they are, and the browser's one prompt is worth more
          // spent on a gesture that means "where am I" — which on this page is
          // the GO button in this column, and only that: the locate control is
          // not drawn here (see the CompassControl gate in ExploreMap).
          askLocation="never"
          // A STATION PAGE'S URL IS ITS PERMALINK. No hash is read and none is
          // written, maximized or not.
          syncUrl={false}
          // MAXIMIZES IN PLACE — the chart page's own mechanism, reused rather
          // than reinvented: same map instance, same `mapRootStyle` overlay,
          // ✕ / Escape / the button to collapse. No `expandedPath` /
          // `collapsedPath` pair, so `urlExpand` is false and the toggle is
          // local React state only — this page's address never moves.
          expandInPlace
        />
      </AspectRatio>
    </Box>
  )
}
